import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, first, Observable, Subject, Subscription } from 'rxjs';
import { unsubscribeAll } from '../../../common/app.helpers';
import {
  ContextPreferences,
  NotificationsContextPreferences,
  DefaultService as RestApiService,
  ContextName,
  ExtendedNotificationsContextPreferences,
  ExtendedContextPreferences,
} from '../../../swagger-api';
import { ChannelPreferenceByContextName } from './notification_preferences.interfaces';

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesService {
  private notificationPreferencesChanged$: Subject<ChannelPreferenceByContextName> =
    new Subject<ChannelPreferenceByContextName>();

  private subscriptions$: Subscription[] = [];

  private apiService = inject(RestApiService);

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public get notificationPreferencesChanged(): Observable<ChannelPreferenceByContextName> {
    return this.notificationPreferencesChanged$.asObservable();
  }

  public getNotificationPreferences(): void {
    this.subscriptions$.push(
      this.apiService
        .restNotificationsNotificationPreferencesGet()
        .pipe(
          first(),
          catchError(() => EMPTY),
        )
        .subscribe((notificationsContextPreferences: ExtendedNotificationsContextPreferences) => {
          this.emitNotificationPreferencesChanged(notificationsContextPreferences);
        }),
    );
  }

  public updateNotificationPreferences(notificationContextPreferences: ChannelPreferenceByContextName): void {
    this.subscriptions$.push(
      this.apiService
        .restNotificationsNotificationPreferencesPut(
          this.convertChannelPreferencesToNotificationContext(notificationContextPreferences),
        )
        .pipe(
          first(),
          catchError(() => EMPTY),
        )
        .subscribe((notificationsContextPreferences: NotificationsContextPreferences) => {
          this.emitNotificationPreferencesChanged(notificationsContextPreferences);
        }),
    );
  }

  private convertChannelPreferencesToNotificationContext(
    notificationContextPreferences: ChannelPreferenceByContextName,
  ): NotificationsContextPreferences {
    const result: NotificationsContextPreferences = [];
    Object.values(ContextName).forEach((enumName: ContextName) => {
      if (
        !notificationContextPreferences[enumName].emailsEnabled ||
        !notificationContextPreferences[enumName].messagingEnabled
      ) {
        result.push({
          context_name: enumName,
          disabled_channels: this.getDisabledChannels(
            notificationContextPreferences[enumName].emailsEnabled,
            notificationContextPreferences[enumName].messagingEnabled,
          ),
        });
      }
    });
    return result;
  }

  private getDisabledChannels(
    emailsEnabled: boolean,
    messagingEnabled: boolean,
  ): ContextPreferences.DisabledChannelsEnum[] {
    const disabledChannels: ContextPreferences.DisabledChannelsEnum[] = [];

    if (!emailsEnabled) {
      disabledChannels.push(ContextPreferences.DisabledChannelsEnum.Email);
    }

    if (!messagingEnabled) {
      disabledChannels.push(ContextPreferences.DisabledChannelsEnum.Messaging);
    }

    return disabledChannels;
  }

  private emitNotificationPreferencesChanged(
    notificationContextPreferences: ExtendedNotificationsContextPreferences,
  ): void {
    const disabled_channels: ChannelPreferenceByContextName = {};

    Object.values(ContextName).forEach((enumName: ContextName) => {
      const foundContextPreferences: ExtendedContextPreferences | undefined = notificationContextPreferences.find(
        (contextPreferences: ExtendedContextPreferences) => contextPreferences.context_name == enumName,
      );

      disabled_channels[enumName] = {
        emailsEnabled: !foundContextPreferences?.disabled_channels.includes(
          ContextPreferences.DisabledChannelsEnum.Email,
        ),
        messagingEnabled: !foundContextPreferences?.disabled_channels.includes(
          ContextPreferences.DisabledChannelsEnum.Messaging,
        ),
      };
    });
    this.notificationPreferencesChanged$.next(disabled_channels);
  }
}
